import { useState, useEffect, useRef } from 'react';
import { getForecastEDAReport, getForecastEDAPairplotUrl } from '../../api/analytics';

function CorrelationHeatmap({ correlations }) {
  const canvasRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  if (!correlations?.top_feature_pairs?.length) return null;

  const pairs = correlations.top_feature_pairs.slice(0, 40);
  const featureSet = new Set();
  for (const p of pairs) { featureSet.add(p.feature_a); featureSet.add(p.feature_b); }
  const features = Array.from(featureSet).slice(0, 15);
  const n = features.length;
  const cellSize = 36;
  const labelW = 130;
  const labelH = 130;
  const w = labelW + n * cellSize;
  const h = labelH + n * cellSize;

  const matrix = Array.from({ length: n }, () => Array(n).fill(0));
  for (const p of pairs) {
    const i = features.indexOf(p.feature_a);
    const j = features.indexOf(p.feature_b);
    if (i >= 0 && j >= 0) { matrix[i][j] = p.correlation; matrix[j][i] = p.correlation; }
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (i === j) { ctx.fillStyle = '#f3f4f6'; }
        else {
          const v = Math.min(1, Math.abs(matrix[i][j]));
          const intensity = Math.round(255 * (1 - v * 0.85));
          ctx.fillStyle = matrix[i][j] > 0
            ? `rgb(${intensity},${intensity},255)` : `rgb(255,${intensity},${intensity})`;
        }
        ctx.fillRect(labelW + j * cellSize, labelH + i * cellSize, cellSize - 1, cellSize - 1);
      }
    }
    ctx.fillStyle = '#374151'; ctx.font = '10px monospace'; ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
    for (let i = 0; i < n; i++) {
      const lbl = features[i].length > 17 ? features[i].slice(0, 16) + '…' : features[i];
      ctx.fillText(lbl, labelW - 4, labelH + i * cellSize + cellSize / 2);
    }
    ctx.textAlign = 'left';
    for (let j = 0; j < n; j++) {
      const lbl = features[j].length > 17 ? features[j].slice(0, 16) + '…' : features[j];
      ctx.save(); ctx.translate(labelW + j * cellSize + cellSize / 2, labelH - 4); ctx.rotate(-Math.PI / 2);
      ctx.fillText(lbl, 0, 0); ctx.restore();
    }
  }, [correlations]);

  function handleMouseMove(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    const j = Math.floor((e.clientX - rect.left - labelW) / cellSize);
    const i = Math.floor((e.clientY - rect.top - labelH) / cellSize);
    if (i >= 0 && i < n && j >= 0 && j < n && i !== j) {
      setTooltip({ x: e.clientX, y: e.clientY, text: `${features[i]} × ${features[j]}: ${matrix[i][j].toFixed(3)}` });
    } else setTooltip(null);
  }

  return (
    <div style={{ overflowX: 'auto', position: 'relative' }}>
      <canvas ref={canvasRef} width={w} height={h}
        onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}
        style={{ cursor: 'crosshair' }} />
      {tooltip && (
        <div style={{ position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 8, background: 'rgba(17,24,39,0.9)', color: '#fff', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', pointerEvents: 'none', zIndex: 9999 }}>
          {tooltip.text}
        </div>
      )}
    </div>
  );
}

function SignalTable({ signal }) {
  if (!signal?.length) return null;
  const maxAbs = Math.max(...signal.map(s => Math.abs(s.correlation)), 0.001);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8125rem' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
            <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: '#374151' }}>Feature</th>
            <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: '#374151' }}>Label correlation</th>
          </tr>
        </thead>
        <tbody>
          {signal.slice(0, 30).map(s => (
            <tr key={s.feature} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '4px 8px', color: '#111827' }}>{s.feature}</td>
              <td style={{ padding: '4px 8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ flex: 1, height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${(Math.abs(s.correlation) / maxAbs) * 100}%`,
                      background: s.correlation >= 0 ? '#2563eb' : '#dc2626', borderRadius: '4px',
                    }} />
                  </div>
                  <span style={{ color: s.correlation >= 0 ? '#1d4ed8' : '#dc2626', minWidth: '56px', textAlign: 'right' }}>
                    {s.correlation.toFixed(3)}
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UMAPScatter({ umap }) {
  const canvasRef = useRef(null);
  if (!umap?.length) return null;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const xs = umap.map(p => p.x), ys = umap.map(p => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const pad = 20;
    const scaleX = (x) => pad + ((x - minX) / (maxX - minX + 1e-9)) * (W - 2 * pad);
    const scaleY = (y) => pad + ((y - minY) / (maxY - minY + 1e-9)) * (H - 2 * pad);

    const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];
    for (const pt of umap) {
      const c = COLORS[Math.abs(pt.label) % COLORS.length];
      ctx.fillStyle = c + 'aa';
      ctx.beginPath();
      ctx.arc(scaleX(pt.x), scaleY(pt.y), 3, 0, 2 * Math.PI);
      ctx.fill();
    }
  }, [umap]);

  return <canvas ref={canvasRef} width={500} height={360} style={{ display: 'block', maxWidth: '100%' }} />;
}

export default function CorrelationEDAReport({ configId, runId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getForecastEDAReport(configId, runId)
      .then(res => { setData(res.data ?? res); setLoading(false); })
      .catch(err => { setError(err.message || 'Failed to load EDA report'); setLoading(false); });
  }, [configId, runId]);

  if (loading) return <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading EDA report…</div>;
  if (error) return <div style={{ color: '#dc2626', fontSize: '0.875rem' }}>{error}</div>;
  if (!data) return null;

  const pairplotUrl = getForecastEDAPairplotUrl(configId, runId);

  return (
    <div style={{ fontSize: '0.875rem' }}>
      {data.correlations && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Feature Correlation Heatmap</div>
          <CorrelationHeatmap correlations={data.correlations} />
        </div>
      )}

      {data.signal && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Feature–Label Signal</div>
          <SignalTable signal={data.signal} />
        </div>
      )}

      {data.umap && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: '8px' }}>UMAP Projection</div>
          <UMAPScatter umap={data.umap} />
          <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '4px' }}>
            2D UMAP of top 50 features (up to 2000 samples). Colors = label class.
          </div>
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Pairplot</div>
        <img src={pairplotUrl} alt="Feature pairplot" style={{ maxWidth: '100%', borderRadius: '6px', border: '1px solid #e5e7eb' }}
          onError={e => { e.currentTarget.style.display = 'none'; }} />
      </div>
    </div>
  );
}
